import { useMemo } from 'react';
import { useAssistantTool } from '@assistant-ui/react';
import {
  fetchContactSubscriptions,
  fetchElementsByNames,
  fetchFormsByNames,
  fetchRecentTransactions,
  searchContacts,
  searchElements,
  searchForms,
  searchSubscriptionDonors,
  searchTransactionDonors,
} from '../../../utils/RocksetQueries/SearchQueries';
import {
  DASHBOARD_ASSISTANT_RUNTIME_MODE,
  MAX_RECORDS_PER_CONTACT,
  MAX_SEARCH_RESULTS_PER_CATEGORY,
  SEARCH_TOOL_CACHE_WINDOW_MS,
  recentSearchToolResults,
} from '../constants';
import { stringToolParameters } from '../lib/assistantToolSchemas';
import { allowedSearchAreas, restrictedResult } from '../lib/permissions';
import { dedupeBy, getMerchantNames, limitItems } from '../lib/helpers';
import {
  createFrontendToolActionMessage,
  createFrontendToolErrorMessage,
} from '../lib/toolActionMessages';
import {
  serializeElement,
  serializeForm,
  serializeSearchContact,
  serializeSubscription,
  serializeTransaction,
} from '../lib/serializers';
import { createCompactSearchToolResult } from '../lib/searchResultBuilders';

export function useSearchDashboardRecordsAction({
  activeSessionId,
  dashboardTimezoneOffset,
  dispatchActionMessage,
  normalizedDashboardTimezone,
  organizationName,
}) {
  const parameters = useMemo(
    () =>
      stringToolParameters({
        name: 'query',
        description: 'Name, email, form name, element name, or other keyword to search for.',
      }),
    [],
  );

  const tool = useMemo(
    () => ({
      toolName: 'search_dashboard_records',
      type: 'frontend',
      description:
        'Search dashboard records across contacts, payments, subscriptions, forms, and elements for the active organization. Use this before opening a record by name or email. After this returns status ok, answer from the returned results instead of calling this again for the same query.',
      parameters,
      execute: async ({ query }) => {
        const keyword = query?.trim?.();
        const cacheKey = `${activeSessionId || 'no-session'}:${organizationName || 'no-org'}:${keyword?.toLowerCase?.() || ''}`;
        const cachedResult = recentSearchToolResults.get(cacheKey);
        if (
          cachedResult &&
          Date.now() - cachedResult.timestamp < SEARCH_TOOL_CACHE_WINDOW_MS
        ) {
          return createCompactSearchToolResult(cachedResult.searchResult, {
            alreadyDisplayed: true,
          });
        }

        const merchants = await getMerchantNames(organizationName);
        const access = allowedSearchAreas();

        if (!keyword || keyword.length < 2) {
          const result = {
            runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE,
            status: 'error',
            message: 'Search query must be at least 2 characters.',
          };
          dispatchActionMessage(createFrontendToolErrorMessage({
            message: result.message,
            status: result.status,
            toolArgs: { query: keyword },
            toolName: 'search_dashboard_records',
            toolResult: result,
          }));
          return result;
        }

        if (merchants.length === 0) {
          const result = {
            runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE,
            status: 'error',
            message: 'No active organization name is available for dashboard search.',
          };
          dispatchActionMessage(createFrontendToolErrorMessage({
            message: result.message,
            status: result.status,
            toolArgs: { query: keyword },
            toolName: 'search_dashboard_records',
            toolResult: result,
          }));
          return result;
        }

        if (!Object.values(access).some(Boolean)) {
          const result = restrictedResult('dashboard records');
          dispatchActionMessage(createFrontendToolErrorMessage({
            message: result.message,
            status: result.status,
            toolArgs: { query: keyword },
            toolName: 'search_dashboard_records',
            toolResult: result,
          }));
          return result;
        }

        const [
          contactsRaw,
          paymentContactsRaw,
          subscriptionContactsRaw,
          formNamesRaw,
          elementNamesRaw,
        ] = await Promise.all([
          access.contacts ? searchContacts(merchants, keyword) : Promise.resolve([]),
          access.payments ? searchTransactionDonors(merchants, keyword) : Promise.resolve([]),
          access.subscriptions ? searchSubscriptionDonors(merchants, keyword) : Promise.resolve([]),
          access.forms ? searchForms(merchants, keyword) : Promise.resolve([]),
          access.elements ? searchElements(merchants, keyword) : Promise.resolve([]),
        ]);

        const contacts = limitItems(dedupeBy(contactsRaw, (contact) => contact.contactID));
        const paymentContacts = limitItems(
          dedupeBy(paymentContactsRaw, (contact) => contact.contactID),
        );
        const subscriptionContacts = limitItems(
          dedupeBy(subscriptionContactsRaw, (contact) => contact.contactID),
        );
        const formNames = limitItems(formNamesRaw);
        const elementNames = limitItems(elementNamesRaw);

        const paymentContactIds = paymentContacts.map((contact) => contact.contactID).filter(Boolean);
        const subscriptionContactIds = subscriptionContacts
          .map((contact) => contact.contactID)
          .filter(Boolean);

        const [transactions, subscriptions, forms, elements] = await Promise.all([
          access.payments && paymentContactIds.length > 0
            ? fetchRecentTransactions(
                merchants,
                paymentContactIds,
                MAX_SEARCH_RESULTS_PER_CATEGORY * MAX_RECORDS_PER_CONTACT,
              )
            : Promise.resolve([]),
          access.subscriptions && subscriptionContactIds.length > 0
            ? fetchContactSubscriptions(
                merchants,
                subscriptionContactIds,
                MAX_SEARCH_RESULTS_PER_CATEGORY * MAX_RECORDS_PER_CONTACT,
              )
            : Promise.resolve([]),
          access.forms && formNames.length > 0
            ? fetchFormsByNames(merchants, formNames, MAX_SEARCH_RESULTS_PER_CATEGORY)
            : Promise.resolve([]),
          access.elements && elementNames.length > 0
            ? fetchElementsByNames(merchants, elementNames, MAX_SEARCH_RESULTS_PER_CATEGORY)
            : Promise.resolve([]),
        ]);

        const searchResult = {
          runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE,
          status: 'ok',
          query: keyword,
          organizationName,
          timezone: normalizedDashboardTimezone,
          timezoneOffset: dashboardTimezoneOffset,
          results: {
            contacts: contacts.map(serializeSearchContact),
            paymentContacts: paymentContacts.map(serializeSearchContact),
            subscriptionContacts: subscriptionContacts.map(serializeSearchContact),
            recentPayments: limitItems(transactions, MAX_SEARCH_RESULTS_PER_CATEGORY).map(
              (transaction) => serializeTransaction(transaction, normalizedDashboardTimezone),
            ),
            subscriptions: limitItems(subscriptions, MAX_SEARCH_RESULTS_PER_CATEGORY).map(
              serializeSubscription,
            ),
            forms: limitItems(forms, MAX_SEARCH_RESULTS_PER_CATEGORY).map(serializeForm),
            elements: limitItems(elements, MAX_SEARCH_RESULTS_PER_CATEGORY).map(serializeElement),
          },
        };
        const displayPayload = {
          renderer: 'dashboard_search_results',
          summary: `Searched dashboard records for "${keyword}"`,
          data: searchResult,
        };
        recentSearchToolResults.set(cacheKey, {
          searchResult,
          timestamp: Date.now(),
        });

        const compactResult = createCompactSearchToolResult(searchResult);
        dispatchActionMessage(createFrontendToolActionMessage({
          actionType: 'search',
          content: displayPayload.summary,
          displayPayload,
          status: compactResult.status,
          toolArgs: { query: keyword },
          toolName: 'search_dashboard_records',
          toolResult: compactResult,
        }));

        return compactResult;
      },
    }),
    [
      activeSessionId,
      dashboardTimezoneOffset,
      dispatchActionMessage,
      normalizedDashboardTimezone,
      organizationName,
      parameters,
    ],
  );

  useAssistantTool(tool);
}
